export const MeasurementSignificanceEnum = {
  CORE: {
    value: 'CORE',
    label: 'Core',
    color: 'rgb(220, 235, 255)',
    colorFamily: 'blue',
    icon: 'star',
  },
  EXTENDED: {
    value: 'EXTENDED',
    label: 'Extended',
    color: 'rgb(235, 225, 255)',
    colorFamily: 'purple',
    icon: 'plus',
  },
};

export const MeasurementTypeEnum = {
  BODY_WEIGHT: {
    displayName: "Body weight",
    description: "mass of body; typically measured in kg",
    defaultUnit: "kg",
    significance: "CORE",
  },
  BODY_HEIGHT: {
    displayName: "Body height",
    description: "height of body; typically measured in cm",
    defaultUnit: "cm",
    significance: "CORE",
  },
  BODY_MASS_INDEX: {
    displayName: "Body mass index",
    description: "weight of body divided by height squared; typically measured in kg/m^2",
    defaultUnit: "kg/m^2",
    significance: "CORE",
  },
  HEART_RATE: {
    displayName: "Heart rate",
    description: "typically measured in bpm",
    defaultUnit: "bpm",
    significance: "CORE",
  },
  URINE_ACR: {
    displayName: "Albumin/creatinine ratio",
    description: "kidney function, albumin vs creatinine ratio",
    defaultUnit: "mg/mmol",
    significance: "CORE",
  },
  HAEMOGLOBIN: {
    displayName: "Haemoglobin estimation",
    description: "standard haemoglobin (Hb) measurement that comes with full blood count (FBC) test",
    defaultUnit: "g/L",
    significance: "CORE",
  },
  HBA1C: {
    displayName: "Hemoglobin A1c estimation",
    description: "average blood glucose over previous 2–3 months",
    defaultUnit: "mmol/mol",
    significance: "CORE",
  },
  SERUM_FERRITIN: {
    displayName: "Serum ferritin level",
    description: "serum ferritin concentration; marker of iron stores; measured in µg/L",
    defaultUnit: "µg/L",
    significance: "CORE",
  },
  VITAMIN_B12: {
    displayName: "Serum vitamin b12 level",
    description: "serum cobalamin (vitamin B12) concentration; measured in ng/L",
    defaultUnit: "ng/L",
    significance: "CORE",
  },
  SERUM_FOLATE: {
    displayName: "Serum folate level",
    description: "serum folate concentration; measured in µg/L",
    defaultUnit: "µg/L",
    significance: "CORE",
  },
  SYSTOLIC_BLOOD_PRESSURE: {
    displayName: "Systolic blood pressure",
    description: "pressure when heart contracts; measured in mmHg",
    defaultUnit: "mmHg",
    significance: "CORE",
  },
  DIASTOLIC_BLOOD_PRESSURE: {
    displayName: "Diastolic blood pressure",
    description: "pressure when heart relaxes; measured in mmHg",
    defaultUnit: "mmHg",
    significance: "CORE",
  },
  CREATININE: {
    displayName: "Creatinine level",
    description: "serum creatinine concentration",
    defaultUnit: "µmol/L",
    significance: "CORE",
  },
  E_GFR: {
    displayName: "Glomerular filtration rate",
    description: "estimated glomerular filtration rate",
    defaultUnit: "mL/min/1.73m^2",
    significance: "CORE",
  },
  ALT: {
    displayName: "Serum alanine aminotransferase level",
    description: "alanine aminotransferase enzyme",
    defaultUnit: "U/L",
    significance: "CORE",
  },
  ALKALINE_PHOSPHATASE: {
    displayName: "Serum alkaline phosphatase level",
    description: "enzyme related to liver and bile ducts",
    defaultUnit: "U/L",
    significance: "CORE",
  },
  CHOLESTEROL: {
    displayName: "Total cholesterol level",
    description: "total cholesterol level",
    defaultUnit: "mmol/L",
    significance: "CORE",
  },
  PSA: {
    displayName: "Prostate specific antigen level",
    description: "prostate-specific antigen for prostate cancer screening and monitoring",
    defaultUnit: "µg/L",
    significance: "CORE",
  },
  PEFR: {
    displayName: "Peak expiratory flow rate",
    description: "peak expiratory flow rate; lung function measure",
    defaultUnit: "L/min",
    significance: "CORE",
  },
  FENO: {
    displayName: "Volume fraction of nitric oxide in exhaled air",
    description: "fractional exhaled nitric oxide; marker of airway inflammation",
    defaultUnit: "ppb",
    significance: "CORE",
  },
  INR_CLOT: {
    displayName: "International normalized ratio",
    description: "international normalized ratio; measure of blood clotting time, commonly used to monitor warfarin therapy",
    defaultUnit: "ratio",
    significance: "CORE",
  },
  PROLACTIN: {
    displayName: "Prolactin level",
    description: "hormone by pituitary gland",
    defaultUnit: "mU/L",
    significance: "CORE",
  },
  T3: {
    displayName: "Serum T3 level",
    description: "part of thyroid test suite",
    defaultUnit: "pmol/L",
    significance: "CORE",
  },
  T4: {
    displayName: "Serum T4 level",
    description: "part of thyroid test suite",
    defaultUnit: "pmol/L",
    significance: "CORE",
  },
  TSH: {
    displayName: "Serum thyroid stimulating hormone level",
    description: "part of thyroid test suite",
    defaultUnit: "mU/L",
    significance: "CORE",
  },
  WBC_COUNT: {
    displayName: "Count of white blood cells in fluid",
    description: "total white blood cell count; measured in ×10^9/L",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  NEUTROPHIL_COUNT: {
    displayName: "Neutrophil count",
    description: "absolute neutrophil count; measured in ×10^9/L",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  LYMPHOCYTE_COUNT: {
    displayName: "Lymphocyte count",
    description: "absolute lymphocyte count; measured in ×10^9/L",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  EOSINOPHIL_COUNT: {
    displayName: "Eosinophil count",
    description: "absolute eosinophil count; measured in ×10^9/L",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  MONOCYTE_COUNT: {
    displayName: "Monocyte count",
    description: "absolute monocyte count; measured in ×10^9/L",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  BASOPHIL_COUNT: {
    displayName: "Basophil count",
    description: "absolute basophil count; measured in ×10^9/L",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  PLATELET_COUNT: {
    displayName: "Platelet count",
    description: "platelet (thrombocyte) count from FBC; measured in ×10^9/L",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  MCV: {
    displayName: "Mean corpuscular volume",
    description: "mean corpuscular volume; size of red cells; measured in fL",
    defaultUnit: "fL",
    significance: "EXTENDED",
  },
  MCH: {
    displayName: "Mean corpuscular haemoglobin",
    description: "mean cell haemoglobin; haemoglobin per red cell; measured in pg",
    defaultUnit: "pg",
    significance: "EXTENDED",
  },
  HAEMATOCRIT: {
    displayName: "Haematocrit",
    description: "packed cell volume as a fraction of whole blood; measured in L/L or %",
    defaultUnit: "L/L",
    significance: "EXTENDED",
  },
  RETICULOCYTE_COUNT: {
    displayName: "Reticulocyte count",
    description: "immature red cell count; marker of bone marrow erythropoietic activity; measured in ×10^9/L or %",
    defaultUnit: "×10^9/L",
    significance: "EXTENDED",
  },
  SERUM_IRON: {
    displayName: "Serum iron level",
    description: "serum iron concentration; measured in µmol/L",
    defaultUnit: "µmol/L",
    significance: "EXTENDED",
  },
  TIBC: {
    displayName: "Serum total iron binding capacity",
    description: "total iron-binding capacity; measured in µmol/L",
    defaultUnit: "µmol/L",
    significance: "EXTENDED",
  },
  SERUM_SODIUM: {
    displayName: "Serum sodium level",
    description: "serum sodium concentration; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  SERUM_POTASSIUM: {
    displayName: "Serum potassium level",
    description: "serum potassium concentration; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  SERUM_UREA: {
    displayName: "Serum urea level",
    description: "serum urea concentration; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  AST: {
    displayName: "Aspartate aminotransferase level",
    description: "aspartate aminotransferase enzyme; measured in U/L",
    defaultUnit: "U/L",
    significance: "EXTENDED",
  },
  GGT: {
    displayName: "Gamma-glutamyl transferase level",
    description: "gamma-glutamyl transferase enzyme; marker of liver disease and alcohol use; measured in U/L",
    defaultUnit: "U/L",
    significance: "EXTENDED",
  },
  TOTAL_BILIRUBIN: {
    displayName: "Serum total bilirubin level",
    description: "total serum bilirubin; measured in µmol/L",
    defaultUnit: "µmol/L",
    significance: "EXTENDED",
  },
  SERUM_ALBUMIN: {
    displayName: "Serum albumin level",
    description: "serum albumin concentration; marker of synthetic liver function and nutritional status; measured in g/L",
    defaultUnit: "g/L",
    significance: "EXTENDED",
  },
  TOTAL_PROTEIN: {
    displayName: "Serum total protein",
    description: "total serum protein concentration; measured in g/L",
    defaultUnit: "g/L",
    significance: "EXTENDED",
  },
  ADJUSTED_CALCIUM: {
    displayName: "Plasma corrected calcium level",
    description: "albumin-adjusted serum calcium; corrects total calcium for albumin concentration; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  SERUM_CALCIUM: {
    displayName: "Serum calcium level",
    description: "total serum calcium concentration; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  VITAMIN_D_25_OH: {
    displayName: "25-Hydroxyvitamin D3 level",
    description: "serum 25-hydroxyvitamin D concentration; deficiency defined as <25 nmol/L; measured in nmol/L",
    defaultUnit: "nmol/L",
    significance: "EXTENDED",
  },
  PARATHYROID_HORMONE: {
    displayName: "Parathyroid hormone level",
    description: "serum intact parathyroid hormone (PTH) concentration; measured in pmol/L",
    defaultUnit: "pmol/L",
    significance: "EXTENDED",
  },
  DEXA_T_SCORE_LUMBAR: {
    displayName: "Lumbar spine DEXA scan T score",
    description: "lumbar spine BMD T-score; measured in standard deviations (SD)",
    defaultUnit: "SD",
    significance: "EXTENDED",
  },
  DEXA_T_SCORE_HIP: {
    displayName: "Hip DEXA scan T score",
    description: "total hip BMD T-score; measured in standard deviations (SD)",
    defaultUnit: "SD",
    significance: "EXTENDED",
  },
  DEXA_T_SCORE_FEMORAL_NECK: {
    displayName: "Femoral neck DXA scan T score",
    description: "femoral neck BMD T-score; measured in standard deviations (SD)",
    defaultUnit: "SD",
    significance: "EXTENDED",
  },
  HDL_CHOLESTEROL: {
    displayName: "Non high density lipoprotein cholesterol level",
    description: "high-density lipoprotein cholesterol; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  LDL_CHOLESTEROL: {
    displayName: "Serum low density lipoprotein cholesterol level",
    description: "low-density lipoprotein cholesterol; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  NON_HDL_CHOLESTEROL: {
    displayName: "Non high density lipoprotein cholesterol level",
    description: "non-HDL cholesterol (total cholesterol minus HDL); NHS secondary-prevention target <2.5 mmol/L; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  TRIGLYCERIDES: {
    displayName: "Serum triglycerides level",
    description: "serum triglyceride concentration; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  CRP: {
    displayName: "Serum C reactive protein level",
    description: "C-reactive protein; acute-phase inflammatory marker; measured in mg/L",
    defaultUnit: "mg/L",
    significance: "EXTENDED",
  },
  ESR: {
    displayName: "Erythrocyte sedimentation rate",
    description: "erythrocyte sedimentation rate; non-specific inflammatory marker; measured in mm/hr",
    defaultUnit: "mm/hr",
    significance: "EXTENDED",
  },
  APTT: {
    displayName: "Activated partial thromboplastin time",
    description: "activated partial thromboplastin time; measures intrinsic clotting pathway; measured in seconds",
    defaultUnit: "s",
    significance: "EXTENDED",
  },
  PROTHROMBIN_TIME: {
    displayName: "Prothrombin time",
    description: "prothrombin time reported in seconds (distinct from INR); measured in seconds",
    defaultUnit: "s",
    significance: "EXTENDED",
  },
  FIBRINOGEN: {
    displayName: "Mass concentration of fibrinogen in plasma",
    description: "plasma fibrinogen concentration; measured in g/L",
    defaultUnit: "g/L",
    significance: "EXTENDED",
  },
  D_DIMER: {
    displayName: "D-dimer level",
    description: "D-dimer concentration; marker of fibrin degradation; measured in µg/L FEU",
    defaultUnit: "µg/L FEU",
    significance: "EXTENDED",
  },
  BNP: {
    displayName: "Plasma pro-brain natriuretic peptide level",
    description: "B-type natriuretic peptide; measured in pg/mL; NICE NG106 thresholds: 100 pg/mL and 400 pg/mL",
    defaultUnit: "ng/L",
    significance: "EXTENDED",
  },
  LVEF: {
    displayName: "Left ventricular ejection fraction",
    description: "left ventricular ejection fraction from echocardiogram or cardiac MRI; HFrEF ≤40%, HFmrEF 41–49%, HFpEF ≥50% per NICE NG106; measured in %",
    defaultUnit: "%",
    significance: "EXTENDED",
  },
  FEV1_ABSOLUTE: {
    displayName: "Forced expiratory volume in 1 second z-score",
    description: "forced expiratory volume in 1 second (absolute value); measured in L",
    defaultUnit: "L",
    significance: "EXTENDED",
  },
  FEV1_PERCENT_PREDICTED: {
    displayName: "Percent predicted fev1",
    description: "FEV1 as a percentage of age/sex/height predicted value; COPD severity bands: ≥80%, 50–79%, 30–49%, <30%; measured in %",
    defaultUnit: "%",
    significance: "EXTENDED",
  },
  FVC_ABSOLUTE: {
    displayName: "Target forced vital capacity",
    description: "forced vital capacity (absolute value); measured in L",
    defaultUnit: "L",
    significance: "EXTENDED",
  },
  FVC_PERCENT_PREDICTED: {
    displayName: "Forced vital capacity",
    description: "FVC as a percentage of predicted; used in restrictive disease monitoring; measured in %",
    defaultUnit: "%",
    significance: "EXTENDED",
  },
  FEV1_FVC_RATIO: {
    displayName: "Expected fev1/fvc ratio",
    description: "ratio of FEV1 to FVC; post-bronchodilator value <0.7 defines airflow obstruction; measured as a ratio",
    defaultUnit: "ratio",
    significance: "EXTENDED",
  },
  OXYGEN_SATURATION: {
    displayName: "Peripheral oxygen saturation",
    description: "peripheral oxygen saturation (SpO2) by pulse oximetry; measured in %",
    defaultUnit: "%",
    significance: "EXTENDED",
  },
  FASTING_GLUCOSE: {
    displayName: "Plasma fasting glucose level",
    description: "fasting plasma glucose concentration; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  TOTAL_TESTOSTERONE: {
    displayName: "Serum testosterone level",
    description: "total serum testosterone concentration; measured in nmol/L",
    defaultUnit: "nmol/L",
    significance: "EXTENDED",
  },
  SHBG: {
    displayName: "Serum sex hormone binding globulin level",
    description: "sex hormone-binding globulin; measured in nmol/L",
    defaultUnit: "nmol/L",
    significance: "EXTENDED",
  },
  OESTRADIOL: {
    displayName: "Serum oestradiol level",
    description: "serum 17β-oestradiol concentration; measured in pmol/L",
    defaultUnit: "pmol/L",
    significance: "EXTENDED",
  },
  FSH: {
    displayName: "Follicle stimulating hormone level",
    description: "follicle-stimulating hormone; measured in IU/L",
    defaultUnit: "IU/L",
    significance: "EXTENDED",
  },
  LH: {
    displayName: "Luteinising hormone level",
    description: "luteinising hormone; measured in IU/L",
    defaultUnit: "IU/L",
    significance: "EXTENDED",
  },
  FAECAL_CALPROTECTIN: {
    displayName: "Faecal calprotectin content",
    description: "faecal calprotectin concentration; marker of intestinal inflammation; NICE DG11 IBD vs IBS cut-off 50 µg/g; measured in µg/g",
    defaultUnit: "µg/g",
    significance: "EXTENDED",
  },
  FAECAL_HAEMOGLOBIN_FIT: {
    displayName: "Quantitative faecal immunochemical test",
    description: "faecal immunochemical test (FIT) haemoglobin; colorectal cancer 2-week-wait pathway; NICE DG56/NG12 referral threshold ≥10 µg Hb/g faeces; measured in µg Hb/g",
    defaultUnit: "µg Hb/g",
    significance: "EXTENDED",
  },
  SERUM_AMYLASE: {
    displayName: "Serum amylase level",
    description: "serum amylase concentration; marker of pancreatic inflammation; measured in U/L",
    defaultUnit: "U/L",
    significance: "EXTENDED",
  },
  SERUM_LIPASE: {
    displayName: "Serum lipase level",
    description: "serum lipase concentration; pancreatic marker, preferred over amylase in some centres; measured in U/L",
    defaultUnit: "U/L",
    significance: "EXTENDED",
  },
  RHEUMATOID_FACTOR: {
    displayName: "Rheumatoid factor",
    description: "rheumatoid factor titre; measured in IU/mL",
    defaultUnit: "IU/mL",
    significance: "EXTENDED",
  },
  ANTI_CCP: {
    displayName: "Anti-cyclic citrullinated peptide antibody level",
    description: "anti-cyclic citrullinated peptide antibody; measured in U/mL",
    defaultUnit: "U/mL",
    significance: "EXTENDED",
  },
  CREATINE_KINASE: {
    displayName: "Serum creatine kinase level",
    description: "total creatine kinase concentration; elevated in myopathy, myositis and statin-induced muscle damage; measured in U/L",
    defaultUnit: "U/L",
    significance: "EXTENDED",
  },
  CA125: {
    displayName: "Cancer antigen 125 level",
    description: "cancer antigen 125; ovarian cancer pathway; NICE NG12 age-banded referral thresholds; measured in IU/mL",
    defaultUnit: "IU/mL",
    significance: "EXTENDED",
  },
  CA19_9: {
    displayName: "Cancer antigen 19-9 level",
    description: "carbohydrate antigen 19-9; pancreatic and biliary tract cancer marker; measured in U/mL",
    defaultUnit: "U/mL",
    significance: "EXTENDED",
  },
  CEA: {
    displayName: "Carcinoembryonic antigen level",
    description: "carcinoembryonic antigen; colorectal cancer surveillance and lung adenocarcinoma; measured in µg/L",
    defaultUnit: "µg/L",
    significance: "EXTENDED",
  },
  LDH: {
    displayName: "Lactate dehydrogenase level",
    description: "lactate dehydrogenase; elevated in lymphoma, melanoma, haemolysis and germ-cell tumours; measured in U/L",
    defaultUnit: "U/L",
    significance: "EXTENDED",
  },
  SERUM_LITHIUM: {
    displayName: "Serum lithium level",
    description: "plasma lithium concentration; therapeutic range monitoring; measured in mmol/L",
    defaultUnit: "mmol/L",
    significance: "EXTENDED",
  },
  INTRAOCULAR_PRESSURE: {
    displayName: "Intraocular pressure",
    description: "intraocular pressure measured by Goldmann applanation tonometry; reported in glaucoma clinic letters; measured in mmHg",
    defaultUnit: "mmHg",
    significance: "EXTENDED",
  },
  INTRAOCULAR_PRESSURE_LEFT_EYE: {
    displayName: "Intraocular pressure of left eye",
    description: "intraocular pressure measured in left eye; reported in glaucoma clinic letters; measured in mmHg",
    defaultUnit: "mmHg",
    significance: "EXTENDED",
  },
  INTRAOCULAR_PRESSURE_RIGHT_EYE: {
    displayName: "Intraocular pressure of right eye",
    description: "intraocular pressure measured in right eye; reported in glaucoma clinic letters; measured in mmHg",
    defaultUnit: "mmHg",
    significance: "EXTENDED",
  },
  VISUAL_ACUITY_LEFT_EYE: {
    displayName: "Visual acuity - left eye",
    description: "visual acuity reading for the left eye; coded as a specific SNOMED concept per reading",
    significance: "EXTENDED",
    valueType: "NONE",
  },
  VISUAL_ACUITY_RIGHT_EYE: {
    displayName: "Visual acuity - right eye",
    description: "visual acuity reading for the right eye; coded as a specific SNOMED concept per reading",
    significance: "EXTENDED",
    valueType: "NONE",
  },
};
